import { jsx } from 'react/jsx-runtime';
import { forwardRef, useMemo } from 'react';
import { Slider } from '../slider/slider.js';
import { makeHueSliderStyle } from './hue-slider.utils.js';

/** The underlying slider works in whole degrees; the public value is normalized to [0, 1]. */
const DEGREES = 360;
/**
 * The max slider value, one step short of a full turn. Both 0° and 360° are the same hue (red),
 * so capping here stops a drag or keyboard loop from round-tripping through a duplicate value and
 * making the thumb appear to jump.
 */
const MAX_DEGREES = DEGREES - 1;
/**
 * A slider for adjusting the hue of a color. Built on {@link Slider}, with a track showing the
 * hue wheel as a gradient and a thumb painted with the current hue.
 * @see https://fpl.figma.design/components/hue-slider/
 */
const HueSlider = forwardRef(({ value, onChange, colorSpace = 'srgb', size = 'md', ...props }, ref) => {
    const degrees = value * DEGREES;
    const style = useMemo(() => makeHueSliderStyle(value, colorSpace), [value, colorSpace]);
    return (jsx(Slider, { "data-fpl-component": "", ...props, ref: ref, size: size, loop: true, min: 0, max: MAX_DEGREES, step: 1, bigStep: 10, value: degrees, 
        // Announce the hue as a degree so screen readers get a friendlier value than a fraction.
        valueText: `${Math.min(Math.round(degrees), MAX_DEGREES)}°`, onChange: (next, other) => onChange?.(next / DEGREES, other), style: style }));
});
HueSlider.displayName = 'HueSlider';

export { HueSlider };
//# sourceMappingURL=hue-slider.js.map
